import { useState, useEffect, Component } from 'react';
import { Player } from '@remotion/player';
import { transform } from 'sucrase';
import * as Remotion from 'remotion';
import * as RemotionTransitions from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import React from 'react';

/**
 * Error Boundary to catch runtime errors during component rendering
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  onError: (error: string) => void;
  fallback: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: string | null;
}

class PreviewErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error: error.message };
  }

  componentDidCatch(error: Error) {
    console.error('[Preview] Runtime error:', error.message);
    this.props.onError(error.message);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface RemotionPreviewProps {
  code: string;
  componentName: string;
  fps: number;
  width: number;
  height: number;
  durationInSeconds: number;
  onError?: (error: string) => void;
  onSuccess?: () => void;
}

interface PreviewState {
  component: React.ComponentType | null;
  error: string | null;
}

/**
 * Strip import statements and prepare code for execution
 */
function prepareCode(code: string): string {
  // Remove all import statements (single and multi-line)
  // Handles: import x from 'y', import {x} from 'y', import * as x from 'y'
  let prepared = code
    // Multi-line imports: import {\n  x,\n  y\n} from 'z'
    .replace(/^import\s*\{[\s\S]*?\}\s*from\s*['"][^'"]+['"];?\s*$/gm, '')
    // Single line imports: import x from 'y' or import {x} from 'y'
    .replace(/^import\s+.*?from\s*['"][^'"]+['"];?\s*$/gm, '')
    // Side-effect imports: import 'x'
    .replace(/^import\s*['"][^'"]+['"];?\s*$/gm, '')
    // Type imports: import type {x} from 'y'
    .replace(/^import\s+type\s+.*?from\s*['"][^'"]+['"];?\s*$/gm, '');

  // Remove 'export default' but keep the declaration
  prepared = prepared.replace(/export\s+default\s+/g, '');

  // Replace 'export const' with just 'const' and track for later
  prepared = prepared.replace(/export\s+const\s+/g, 'const __export__ ');

  // Replace 'export function' with just 'function' and track
  prepared = prepared.replace(/export\s+function\s+/g, 'function __export__ ');

  return prepared;
}

/**
 * Extract exported component names from prepared code
 */
function extractExportedNames(code: string): string[] {
  const names: string[] = [];
  const constMatch = code.matchAll(/const\s+__export__\s+(\w+)/g);
  const funcMatch = code.matchAll(/function\s+__export__\s+(\w+)/g);

  for (const match of constMatch) {
    names.push(match[1]);
  }
  for (const match of funcMatch) {
    names.push(match[1]);
  }

  return names;
}

export function RemotionPreview({
  code,
  componentName,
  fps,
  width,
  height,
  durationInSeconds,
  onError,
  onSuccess,
}: RemotionPreviewProps) {
  const [state, setState] = useState<PreviewState>({
    component: null,
    error: null,
  });

  const durationInFrames = fps * durationInSeconds;

  useEffect(() => {
    if (!code) {
      setState({ component: null, error: null });
      return;
    }

    console.log('\n' + '='.repeat(60));
    console.log('🎬 [PREVIEW] Starting code compilation...');
    console.log('='.repeat(60));
    console.log('📝 [INPUT] Component name:', componentName);
    console.log('📝 [INPUT] Code length:', code.length, 'chars');
    console.log('📝 [INPUT] First 300 chars:\n', code.slice(0, 300));

    try {
      // Prepare code by stripping imports
      const prepared = prepareCode(code);
      const exportedNames = extractExportedNames(prepared);

      console.log('\n📦 [EXPORTS] Found exports:', exportedNames);

      // Clean up the __export__ markers
      const cleanCode = prepared
        .replace(/const\s+__export__\s+/g, 'const ')
        .replace(/function\s+__export__\s+/g, 'function ');

      console.log('\n🧹 [CLEANED] Code after import stripping (first 500 chars):\n', cleanCode.slice(0, 500));

      // Transpile TSX to JS (no imports/exports transform since we stripped them)
      const transpiled = transform(cleanCode, {
        transforms: ['typescript', 'jsx'],
        jsxRuntime: 'classic',
        production: true,
        disableESTransforms: true,
      }).code;

      console.log('\n⚡ [TRANSPILED] Code length:', transpiled.length, 'chars');

      // Debug: check for remaining imports
      if (transpiled.includes('import ')) {
        console.error('⚠️ [WARNING] Transpiled code still contains import:', transpiled.slice(0, 1000));
      }

      // Create execution context with React and Remotion available
      const context: Record<string, unknown> = {
        React,
        useState: React.useState,
        useEffect: React.useEffect,
        useMemo: React.useMemo,
        useCallback: React.useCallback,
        useRef: React.useRef,
        ...Remotion,
        // @remotion/transitions support
        ...RemotionTransitions,
        fade,
        slide,
        wipe,
      };

      // Build the executable code that captures exports safely (one by one)
      // This prevents "X is not defined" errors when Gemini generates multiple exports
      // but some are not actually defined in the code
      const captureExports = exportedNames.length > 0
        ? `\ncontext.__exports = {};\n${exportedNames.map(name =>
            `if (typeof ${name} !== 'undefined') context.__exports.${name} = ${name};`
          ).join('\n')}`
        : '';

      const wrappedCode = `
        const {
          React, useState, useEffect, useMemo, useCallback, useRef,
          useCurrentFrame, useVideoConfig, interpolate, spring,
          Sequence, Series, AbsoluteFill, Easing, Composition,
          staticFile, continueRender, delayRender, random,
          Loop, Audio, Video, Img, OffthreadVideo,
          getInputProps, measureSpring, interpolateColors,
          TransitionSeries, linearTiming, springTiming,
          fade, slide, wipe
        } = context;

        ${transpiled}
        ${captureExports}
      `;

      console.log('\n🔧 [WRAPPED] Capture exports:', captureExports || 'none');

      // Execute the code
      console.log('\n🚀 [EXECUTE] Running code...');
      const fn = new Function('context', wrappedCode);
      fn(context);
      console.log('✅ [EXECUTE] Code executed successfully');

      // Find the component from exports or by name
      const exports = (context.__exports || {}) as Record<string, unknown>;
      console.log('\n📤 [EXPORTS] Available exports:', Object.keys(exports));

      const Component = exports[componentName] ||
        Object.values(exports).find((exp) => typeof exp === 'function');

      if (!Component || typeof Component !== 'function') {
        throw new Error(`Component "${componentName}" not found. Exported: ${Object.keys(exports).join(', ') || 'none'}`);
      }

      console.log('✅ [SUCCESS] Component found:', componentName);
      console.log('='.repeat(60) + '\n');

      setState({ component: Component as React.ComponentType, error: null });
      onSuccess?.();
    } catch (err) {
      console.error('\n❌ [ERROR] Preview compilation failed:');
      console.error(err);
      console.log('='.repeat(60) + '\n');
      const errorMessage = err instanceof Error ? err.message : String(err);
      setState({
        component: null,
        error: errorMessage,
      });
      onError?.(errorMessage);
    }
  }, [code, componentName, onError, onSuccess]);

  if (state.error) {
    return (
      <div className="flex h-full items-center justify-center bg-red-950 p-4">
        <div className="max-w-md text-center">
          <p className="font-medium text-red-400">Preview Failed</p>
          <p className="mt-2 text-sm text-red-300">{state.error}</p>
          <p className="mt-3 text-xs text-red-400/70">
            Your code is safe. You can still copy it from the Code tab below.
          </p>
        </div>
      </div>
    );
  }

  if (!state.component) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-900">
        <p className="text-slate-500">Loading preview...</p>
      </div>
    );
  }

  const errorFallback = (
    <div className="flex h-full items-center justify-center bg-red-950 p-4">
      <div className="max-w-md text-center">
        <p className="font-medium text-red-400">Preview Failed</p>
        <p className="mt-2 text-sm text-red-300">Runtime error during rendering</p>
        <p className="mt-3 text-xs text-red-400/70">
          Your code is safe. You can still copy it from the Code tab below.
        </p>
      </div>
    </div>
  );

  // Use code hash as key to reset ErrorBoundary when code changes
  const codeKey = code ? code.slice(0, 100) + code.length : 'empty';

  return (
    <PreviewErrorBoundary
      key={codeKey}
      onError={(error) => {
        setState({ component: null, error });
        onError?.(error);
      }}
      fallback={errorFallback}
    >
      <Player
        component={state.component}
        durationInFrames={durationInFrames}
        fps={fps}
        compositionWidth={width}
        compositionHeight={height}
        style={{ width: '100%', height: '100%' }}
        controls
        autoPlay
        loop
      />
    </PreviewErrorBoundary>
  );
}
